// Remix lineage for the class gallery (feature "gallery", decision 56). A gallery entry may point at
// the entry it was remixed from; these helpers walk that family tree and write the credit line.

export interface Lineage {
  id: string;
  remixedFrom: { id: string } | null;
}

export interface LineageNode<T> {
  entry: T;
  children: LineageNode<T>[];
}

/** The top of an entry's family: walk up remixedFrom while the parent is in the list. */
export function familyRoot<T extends Lineage>(entries: T[], id: string): T | undefined {
  const byId = new Map(entries.map((e) => [e.id, e]));
  const seen = new Set<string>();
  let cur = byId.get(id);
  while (cur && cur.remixedFrom && byId.has(cur.remixedFrom.id) && !seen.has(cur.id)) {
    seen.add(cur.id);
    cur = byId.get(cur.remixedFrom.id);
  }
  return cur;
}

/** The tree under one entry: its remixes, then their remixes, children in the order given. */
export function lineageTree<T extends Lineage>(entries: T[], rootId: string): LineageNode<T> | null {
  const byId = new Map(entries.map((e) => [e.id, e]));
  const kids = new Map<string, T[]>();
  for (const e of entries) {
    if (e.remixedFrom) kids.set(e.remixedFrom.id, [...(kids.get(e.remixedFrom.id) ?? []), e]);
  }
  const seen = new Set<string>(); // a loop in the data can't make the tree run forever
  const build = (e: T): LineageNode<T> => {
    seen.add(e.id);
    const children = (kids.get(e.id) ?? []).filter((k) => !seen.has(k.id));
    return { entry: e, children: children.map(build) };
  };
  const root = byId.get(rootId);
  return root ? build(root) : null;
}

/**
 * Adds "Remixed from: <title> by <byline>" under ## About (the credit line). Earlier credit lines
 * stay, so a remix of a remix credits the whole chain. A project with no About section gets one.
 */
export function addRemixCredit(md: string, from: { title: string; byline: string }): string {
  const clean = (s: string) => s.replace(/[\r\n]+/g, " ").trim();
  const line = `Remixed from: ${clean(from.title)} by ${clean(from.byline)}`;
  const about = /^(##\s+About\s*$)([\s\S]*?)(?=^##\s|(?![\s\S]))/im;
  let out = md.trim() ? md : "# PROJECT.md\n";
  if (about.test(out)) {
    out = out.replace(about, (_m, head: string, body: string) => `${head}${body.replace(/\s+$/, "")}\n${line}\n\n`);
  } else {
    const block = `## About\n${line}\n`;
    out = /^#\s.*$/m.test(out) ? out.replace(/^#\s.*$/m, (h) => `${h}\n\n${block}`) : `${block}\n${out}`;
  }
  return out.replace(/\n{3,}/g, "\n\n");
}

// A credit line names a classmate, and names never go to a model provider (CLAUDE.md). The helper
// and "Surprise me" see "by a classmate" instead; after a helper turn the real names go back in.
const CREDIT_LINE = /^(\s*Remixed from: .*) by (.+?)\s*$/;
const HIDDEN_BY = "a classmate";

const hideLine = (line: string) => {
  const m = CREDIT_LINE.exec(line);
  return m ? `${m[1]} by ${HIDDEN_BY}` : line;
};

/** PROJECT.md with each "Remixed from: <title> by <name>" credit reading "by a classmate". */
export function hideRemixCredits(md: string): string {
  return md.split("\n").map(hideLine).join("\n");
}

/**
 * Puts the names back after the helper worked on the hidden copy: each hidden credit line in
 * `edited` that matches one in `original` gets the original line back. Lines the helper removed
 * or rewrote stay as the helper left them.
 */
export function restoreRemixCredits(original: string, edited: string): string {
  const waiting = new Map<string, string[]>();
  for (const line of original.split("\n")) {
    const hidden = hideLine(line);
    if (hidden !== line) waiting.set(hidden, [...(waiting.get(hidden) ?? []), line]);
  }
  if (waiting.size === 0) return edited;
  return edited
    .split("\n")
    .map((line) => waiting.get(line)?.shift() ?? line)
    .join("\n");
}
